import fontkit from "@pdf-lib/fontkit"
import { CacheFunction } from "@pdfking/storage"
import { BlobToUint8, Identity, Uint8ToBlob } from "@pdfking/tfunc"
import type {
  PageBoundingBox,
  PDFEmbeddedPage,
  PDFFont,
  PDFPage,
} from "pdf-lib"
import type { CMYK, RGB } from "pdf-lib"
import {
  closePath,
  cmyk,
  degrees,
  lineTo,
  moveTo,
  PDFDocument,
  popGraphicsState,
  pushGraphicsState,
  setLineWidth,
  setStrokingColor,
  stroke,
} from "pdf-lib"

import { ToPdflibColor } from "../colors/ToPdflibColor"
import { HorizontalAlign } from "../types/align/HorizontalAlign"
import { VerticalAlign } from "../types/align/VerticalAlign"
import type { Page } from "../types/geometry/Page"
import type { Point } from "../types/geometry/Point"
import type { Rectangle } from "../types/geometry/Rectangle"
import type { SpatialRectangle } from "../types/geometry/SpatialRectangle"
import type { PlottingContext } from "../types/options/PlottingContext"
import { PlotFunction } from "../types/plot/PlotFunction"
import type { PlotMedia } from "../types/plot/PlotMedia"
import type { PlotPath } from "../types/plot/PlotPath"
import type { PlotRect } from "../types/plot/PlotRect"
import type { PlotShape } from "../types/plot/PlotShape"
import type { PlotText } from "../types/plot/PlotText"
import type { Scale } from "../types/scale/Scale"
import { BytesToPdf } from "../utils/BytesToPdf"
import { ComputeScale } from "../utils/ComputeScale"
import { CreateContext } from "../utils/CreateContext"
import { Rotate } from "../utils/Rotate"

/**
 * Type representing the context used in the current implementation
 * of the PlotPage function. This combines the intended PlottingContext
 * type with all additional properties actually used in the code.
 */

type ColorAlpha = {
  color: CMYK | RGB | undefined
  opacity: number
}

type PageBlob = {
  page: PDFPage
  scale: Scale
}

type CurrentPlottingContext = {
  // PDF-lib related objects
  doc: PDFDocument
  page: PDFPage
  font: PDFFont
  scale_bleed: number

  // Coordinate transformation methods
  get_xy: (point: Point) => Point
  get_measures: (position: SpatialRectangle) => SpatialRectangle
  get_color: (id: string) => ColorAlpha

  // PDF page retrieval methods
  get_page: (blob_id: string) => Promise<PageBlob | undefined>
  get_embedded_page: (
    page: PDFPage,
    mask: PageBoundingBox
  ) => Promise<PDFEmbeddedPage>
} & Pick<Required<PlottingContext>, "font_height_adjustment" | "stroke_width">

const transparent = cmyk(0, 0, 0, 0) // undefined // can be white instead but it is better to avoid transparency

function todegree(angle: number) {
  return degrees(-(angle || 0))
}

async function CreateCurrentPlottingContext(
  context: Required<PlottingContext>,
  drawing: Page
): Promise<CurrentPlottingContext> {
  const to_px = (value: number): number => context.unit_to_px * value

  const get_size = ({ width, height }: Rectangle): Rectangle => ({
    width: to_px(width),
    height: to_px(height),
  })

  const get_xy = ({ x: px, y: py }: Point): Point => ({
    x: bleed + to_px(px),
    y: bleed - to_px(py) + height,
  })

  const get_measures = (r: SpatialRectangle): SpatialRectangle => {
    const xy = get_xy(r)
    const size = get_size(r)

    return {
      x: xy.x,
      y: xy.y - size.height,
      ...size,
    }
  }

  const { width, height } = get_size(drawing)
  const bleed = to_px(drawing.bleed ?? context.page_bleed)
  const rotation = todegree(0)

  const get_color = (id: string): ColorAlpha => {
    const color = context.color_map[id]
    return color
      ? { opacity: color.a, color: ToPdflibColor(color) }
      : { opacity: 0, color: undefined }
  }

  const get_page = async function (
    blob_id: string
  ): Promise<PageBlob | undefined> {
    const promise = context.file_map[blob_id]
    if (promise) {
      const blob = await promise
      const doc = await BytesToPdf(
        await BlobToUint8(blob),
        blob.type,
        context.image_resolution
      )
      if (doc.getPageCount() == 1) {
        return { scale: context.scale_map[blob_id], page: doc.getPage(0) }
      }
      throw new Error("PDF must have exactly one page")
    }
    return undefined
  }

  const get_embedded_page = async function (
    page: PDFPage,
    mask: PageBoundingBox
  ): Promise<PDFEmbeddedPage | undefined> {
    // this is done becose an empty page raises an errors
    const { Contents } = page
      ? page.node.normalizedEntries()
      : { Contents: undefined }

    return Contents ? await doc.embedPage(page, mask) : undefined
  }

  const doc = await PDFDocument.create()

  doc.registerFontkit(fontkit)
  const font = await doc.embedFont(
    await fetch(context.font_path).then(res => res.arrayBuffer())
  )

  const page = doc.addPage([width + bleed * 2, height + bleed * 2])
  page.setBleedBox(0, 0, width + bleed * 2, height + bleed * 2)
  page.setTrimBox(bleed, bleed, width, height)
  page.setMediaBox(0, 0, width + bleed * 2, height + bleed * 2)
  page.setRotation(rotation)
  page.setFont(font)

  return {
    doc,
    font,
    page,
    get_xy,
    get_page: CacheFunction(get_page, Identity),
    get_measures,
    get_embedded_page,
    get_color,
    stroke_width: to_px(context.stroke_width),
    scale_bleed: to_px(context.scale_bleed),
    font_height_adjustment: context.font_height_adjustment,
  }
}

async function plot_text(
  context: CurrentPlottingContext,
  info: PlotText
): Promise<void> {
  if (info.stroke_color || info.fill_color) {
    plot_rect(context, { ...info, rotation: 0, func: PlotFunction.PLOT_RECT })
  }

  const fw = context.font.widthOfTextAtSize(info.text, info.font_size)
  const fh =
    context.font.heightAtSize(info.font_size) * context.font_height_adjustment

  const {
    x,
    y,
    width: width,
    height: height,
  } = context.get_measures(info.position)

  const { x: w, y: h } = Rotate(0, 0, width, height, info.rotation)

  let ew = 0
  let eh = 0

  if (info.horizontal_align == HorizontalAlign.LEFT) {
    ew = -(Math.abs(w) - fw) / 2
  } else if (info.horizontal_align == HorizontalAlign.RIGHT) {
    ew = +(Math.abs(w) - fw) / 2
  }
  if (info.vertical_align == VerticalAlign.TOP) {
    eh = +(Math.abs(h) - fh) / 2
  } else if (info.vertical_align == VerticalAlign.BOTTOM) {
    eh = -(Math.abs(h) - fh) / 2
  }

  const coord = Rotate(
    x + width / 2,
    y + height / 2,
    x + width / 2 - fw / 2 + ew,
    y + height / 2 - fh / 2 + eh,
    info.rotation
  )

  context.page.drawText(info.text, {
    ...coord,
    ...context.get_color(info.text_color),
    size: info.font_size,
    rotate: todegree(info.rotation),
    font: context.font,
  })
}

async function plot_rect(
  context: CurrentPlottingContext,
  info: PlotRect
): Promise<void> {
  const fill = context.get_color(info.fill_color)
  const stroke = context.get_color(info.stroke_color)

  const { x, y, width, height } = context.get_measures(info.position)

  const coord = Rotate(x + width / 2, y + height / 2, x, y, info.rotation)

  context.page.drawRectangle({
    ...coord,
    width,
    height,
    rotate: todegree(info.rotation),
    borderWidth: stroke ? context.stroke_width : 0,
    borderColor: stroke ? stroke.color : transparent,
    borderOpacity: stroke ? stroke.opacity : 0,
    color: fill ? fill.color : transparent,
    opacity: fill ? fill.opacity : 0,
  })
}

async function plot_media(
  context: CurrentPlottingContext,
  info: PlotMedia
): Promise<void> {
  const spec = await context.get_page(info.page)

  if (!spec) {
    return
  }

  const inner = spec.page.getTrimBox()
  const media = context.get_measures(info.media_position)
  const crop = context.get_measures(info.crop_position)

  const { x: scale_x, y: scale_y } = ComputeScale(
    { outer: media, inner, bleed: context.scale_bleed },
    spec.scale || undefined
  )

  const { x: tx, y: ty, width: tw, height: th } = inner
  const { x: mx, y: my, width: mw, height: mh } = media
  const { x: cx, y: cy, width: cw, height: ch } = crop

  const center_x = tx + tw / 2
  const center_y = ty + th / 2

  const { x: left, y: bottom } = Rotate(
    center_x,
    center_y,
    center_x - (mx + mw / 2 - cx) / scale_x,
    center_y - (my + mh / 2 - cy) / scale_y,
    -info.rotation
  )
  const { x: right, y: top } = Rotate(
    center_x,
    center_y,
    center_x + (mw / 2 + (cx + cw - (mx + mw))) / scale_x,
    center_y + (mh / 2 + (cy + ch - (my + mh))) / scale_y,
    -info.rotation
  )

  const mask = { top, left, right, bottom }

  const embedded = await context.get_embedded_page(spec.page, mask)

  await context.doc.flush()

  if (embedded)
    context.page.drawPage(embedded, {
      rotate: todegree(info.rotation),
      x: cx,
      y: cy,

      //width: w,
      //height: h,
      //width: 100,
      //height: 100,
      xScale: scale_x,
      yScale: scale_y,
      //opacity: 0.75,
    })
}

async function plot_path(
  context: CurrentPlottingContext,
  info: PlotPath
): Promise<void> {
  const stroke_color = context.get_color(info.stroke_color)
  const [{ x, y }, ...points] = info.points.map(context.get_xy)

  context.page.pushOperators(
    pushGraphicsState(),
    setLineWidth(context.stroke_width),
    setStrokingColor(stroke_color.color),
    moveTo(x, y),
    ...points.map(({ x, y }) => lineTo(x, y)),
    ...(info.closed ? [closePath()] : []),
    stroke(),
    moveTo(0, 0),
    popGraphicsState()
  )
}

const handlers: Record<
  PlotFunction,
  (context: CurrentPlottingContext, shape: PlotShape) => Promise<void>
> = {
  [PlotFunction.PLOT_TEXT]: plot_text,
  [PlotFunction.PLOT_RECT]: plot_rect,
  [PlotFunction.PLOT_MEDIA]: plot_media,
  [PlotFunction.PLOT_PATH]: plot_path,
}

export async function PlotPage(
  page: Page,
  partial: PlottingContext = {}
): Promise<Blob> {
  const context = await CreateCurrentPlottingContext(
    CreateContext(partial),
    page
  )

  for (const shape of page.shapes) {
    await handlers[shape.func](context, shape)
  }

  return context.doc.save().then(bytes => Uint8ToBlob(bytes, "application/pdf"))
}
